import React, { useState } from "react";
import { useRedux } from '../../../../states/redux-state';
import {
    exportReport,
    exportReportPdf,
    exportSummary,
    exportTimeClockReportCsv,
    exportTimeClockReportPdf,
    exportReportXlsx,
    exportTimeClockReportXlsx,
    exportPermissionsPdf,
    exportPermissionsXlsx,
    exportPermissionsCsv,
    exportTravelReport,
    exportActivityReport,
    exportStaticReport,
    exportMileageReport
} from '../report-export-helper';
import "./index.scss";
import { useDispatch } from "react-redux";
import { GMapModalAC } from "../../../../stores/reducers/gMapModal/AC";
import { ReactComponent as PdfSvg } from "../../../../assets/svg/pdf.svg";
import { ReactComponent as CsvSvg } from "../../../../assets/svg/csv.svg";
import { ReactComponent as XlsSvg } from "../../../../assets/svg/xls.svg";
import { Checkbox } from "../../../Checkbox";
import { Button, ButtonSize, ButtonStyles } from "../../../Button";
import { iDevicePingWithGeofences, iList } from "../../../../shared/interfaces";
import { selectDeviceOrPerson, selectReportsGrouped } from "../../../../stores/reducers/report-selectors";
import { exportGeofenceReportCsv, exportGeofenceReportPdf, exportGeofenceReportXlsx } from "../GeofenceReport/helpers";
import useOneDeviceReportUnits from "../ReportPage/useOneDeviceReportUnits";
import { exportExpirationReportCsv, exportExpirationReportPdf, exportExpirationReportXlsx } from "../ExpirationReport/expiration-export";
import { IExpirationGroups } from "../ExpirationReport/utils";

enum ExportFormat {
    PDF = 'pdf',
    XLSX = 'xlsx',
    CSV = 'csv',
}

type ExportReportProps = {
    reportType?: string;
    expirationData?: IExpirationGroups | null;
};

export const ExportReport = ({ reportType: reportTypeOverride, expirationData }: ExportReportProps) => {
    const {
        reportType: reportTypeFromStore,
        reportDetails,
        devicesDetails,
        people,
        allExtraInfo,
        displayRecordsGrouped,
        displayRecords,
        deviceOrPerson,
        geofenceReportsGrouped,
        reportDates,
        permissionPersons,
        travelTripIds,
        mileageDevices,
        mileageStartTime,
        mileageEndTime
    } = useRedux(s => ({
        reportType: s.report.details.reportType,
        reportDetails: s.report.details,
        devicesDetails: s.devicesData.devicesDetails,
        people: s.general.people,
        allExtraInfo: s.general.extraInfo,
        displayRecordsGrouped: selectReportsGrouped(s),
        displayRecords: s.report.displayRecords,
        deviceOrPerson: selectDeviceOrPerson(s),
        geofenceReportsGrouped: selectReportsGrouped(s),
        reportDates: s.report.details.dates,
        permissionPersons: s.report.permissionPersons,
        travelTripIds: s.report.travelTripIds,
        mileageDevices: s.report.mileageDevices,
        mileageStartTime: s.report.mileageStartDate,
        mileageEndTime: s.report.mileageEndDate
    }));

    const reportType = reportTypeOverride || reportTypeFromStore;
    const [exportFormats, setExportFormats] = useState<Record<ExportFormat, boolean>>({
        [ExportFormat.PDF]: false,
        [ExportFormat.XLSX]: false,
        [ExportFormat.CSV]: false,
    });
    const dispatch = useDispatch();
    const deviceUnits = useOneDeviceReportUnits();

    const handleExport = () => {
        const formats = {
            exportPdf: exportFormats[ExportFormat.PDF],
            exportXlsx: exportFormats[ExportFormat.XLSX],
            exportCsv: exportFormats[ExportFormat.CSV],
        };

        if (reportType === 'expiration') {
            if (!expirationData) return;
            if (formats.exportCsv) exportExpirationReportCsv(expirationData);
            if (formats.exportXlsx) exportExpirationReportXlsx(expirationData);
            if (formats.exportPdf) exportExpirationReportPdf(expirationData);
            return;
        }

        if (formats.exportCsv) {
            if (reportType === "geofence") exportGeofenceReportCsv({ devices: devicesDetails, people, deviceOrPerson, geofenceReportsGrouped: (geofenceReportsGrouped as iList<iDevicePingWithGeofences[]>) });
            if (reportType === "time-clock") exportTimeClockReportCsv({ displayRecords: displayRecords, devicesDetails, people })();
            if (reportType === "summary") exportSummary(displayRecordsGrouped, deviceUnits, devicesDetails, reportDates, 'csv');
            if (reportType === "permissions") exportPermissionsCsv(permissionPersons);
        }
        if (formats.exportXlsx) {
            if (reportType === "geofence") exportGeofenceReportXlsx({ devices: devicesDetails, people, deviceOrPerson, geofenceReportsGrouped: (geofenceReportsGrouped as iList<iDevicePingWithGeofences[]>) });
            if (reportType === "time-clock") exportTimeClockReportXlsx({ records: displayRecordsGrouped, reportData: devicesDetails, people });
            if (reportType === "summary") exportSummary(displayRecordsGrouped, deviceUnits, devicesDetails, reportDates, 'xlsx');
            if (reportType === "permissions") exportPermissionsXlsx(permissionPersons);
        }
        if (formats.exportPdf) {
            if (reportType === "geofence") exportGeofenceReportPdf({ devices: devicesDetails, people, deviceOrPerson, geofenceReportsGrouped: (geofenceReportsGrouped as iList<iDevicePingWithGeofences[]>) });
            if (reportType === "time-clock") exportTimeClockReportPdf({ records: displayRecordsGrouped, reportData: devicesDetails, people });
            if (reportType === "summary") exportSummary(displayRecordsGrouped, deviceUnits, devicesDetails, reportDates, 'pdf');
            if (reportType === "permissions") exportPermissionsPdf(permissionPersons)
        }

        if (reportType === "travel") {
            exportTravelReport({ reportData: devicesDetails, report: reportDetails, people, travelTripIds, formats, allExtraInfo })
        }

        if (reportType === 'static') {
            exportStaticReport({ displayRecords, devicesDetails, people, reportDetails, formats });
        }

        if (reportType === 'activity') {
            exportActivityReport({ formats, displayRecords });
        }

        if (reportType === 'mileage') {
            exportMileageReport({ formats, devices: mileageDevices, startTime: mileageStartTime, endTime: mileageEndTime });
        }
    }

    const handleCancel = () => dispatch(GMapModalAC.hideModal());

    const toggleExportFormat = (format: ExportFormat) => {
        setExportFormats(prev => ({
            ...prev,
            [format]: !prev[format],
        }));
    };

    const exportOptions = [
        {
            format: ExportFormat.PDF,
            label: 'PDF',
            Icon: PdfSvg,
        },
        {
            format: ExportFormat.XLSX,
            label: 'XLS',
            Icon: XlsSvg,
        },
        {
            format: ExportFormat.CSV,
            label: 'CSV',
            Icon: CsvSvg,
        },
    ];

    return (
        <div className="export-report-container">
            <div className="export-report-header"><h3>Export report</h3></div>
            <div className="export-report-content">
                {exportOptions.map(({ format, label, Icon }) => (
                    <div className="export-report-content-row" key={format} onClick={() => toggleExportFormat(format)}>
                        <label className={`export-report-label ${exportFormats[format] ? "active" : ""}`}>
                            <Icon style={{fill: '#B00808'}} />&nbsp;{label}
                        </label>
                        <Checkbox checked={exportFormats[format]} />
                    </div>
                ))}
            </div>
            <div className="export-report-actions">
                <Button
                    styleType={ButtonStyles.WHITE_GRAY}
                    size={ButtonSize.LG}
                    onClick={handleCancel}>Cancel</Button>
                <Button
                    styleType={ButtonStyles.BLACK_WHITE}
                    size={ButtonSize.LG}
                    onClick={handleExport}>Export</Button>
            </div>
        </div>
    )
}